import { useEffect, useRef, useState } from "react";
import { useGuestBuiltLoyaltyOffers } from "../../../../services/hooks/loyalty/useGuestBuiltLoyaltyOffers";
import { useCategoryBarLogic } from "../../../../services/hooks/useCategoryBarLogic";
import { DISPLAYED_NAME } from "../../../../utils/enums";
import { getDisplayedName } from "../../../../utils/help/localization/localization";
import { PTab } from "../../../shared/PTab/PTab";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { SPECIALLY_FOR_YOU_ANCHOR } from "../SpeciallyForYou/SpeciallyForYouSection";
import { isVisitor } from "../../../../utils/help/user/user";
import { CategoryType } from "../../../../utils/types";
import styles from "./CategoryBarV2.module.scss";

export const BAR_ID = "category_bar_new";
const SCROLL_HEADER_OFFSET = 150;

export const CategoryBarV2 = (props: {
  categories: CategoryType[];
  areaAboveNavBar: number | undefined;
  searchWrapperBarHeight: number;
  leading?: React.ReactNode;
  leadingRef?: React.RefObject<HTMLDivElement>;
  leadingExpanded?: boolean;
}) => {
  const { isFranchiseOpen, isMainDark, scrollToCategory, trackCategoryClick } =
    useCategoryBarLogic();
  const { t } = useTranslationI18();
  const guestBuiltOffers = useGuestBuiltLoyaltyOffers();

  const initialId = props.categories?.[0]?.id ?? null;
  const [activeCategoryId, setActiveCategoryId] = useState<
    string | number | null
  >(initialId);
  // Ref mirrors state so the scroll handler reads current value without stale closure
  const activeCategoryRef = useRef<string | number | null>(initialId);
  // Prevents the scroll handler from overriding active state during a click-triggered
  // smooth scroll, where intermediate page positions cause wrong categories to match
  const isClickScrollingRef = useRef(false);
  const clickScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Effect: scroll the bar horizontally whenever the active category changes
  // Kept separate from the scroll handler to avoid feedback loops
  useEffect(() => {
    const categoryBarElm = document.querySelector<HTMLElement>(`#${BAR_ID} ul`);
    const navElm = document.querySelector<HTMLElement>(
      `#navElm_new_${activeCategoryId}`
    );

    if (!categoryBarElm || !navElm) {
      return;
    }

    categoryBarElm.scrollTo({
      top: 0,
      left:
        categoryBarElm.scrollLeft +
        navElm.getBoundingClientRect().left -
        categoryBarElm.getBoundingClientRect().left,
      behavior: "smooth",
    });
  }, [activeCategoryId]);

  // Effect: track the active category based on page scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (isClickScrollingRef.current) {
        return;
      }

      const categoryBarElm = document.querySelector<HTMLElement>(
        `#${BAR_ID} ul`
      );
      const offset =
        (categoryBarElm?.getBoundingClientRect().top ?? 0) +
        SCROLL_HEADER_OFFSET;

      props.categories?.forEach((cat) => {
        const section = document.querySelector<HTMLElement>(
          `#category_${cat.id}`
        );
        const sectionTop = Math.trunc(
          section?.getBoundingClientRect().top ?? 0
        );
        const sectionBottom = Math.trunc(
          section?.getBoundingClientRect().bottom ?? 0
        );

        if (sectionTop <= offset && sectionBottom > offset) {
          if (activeCategoryRef.current !== cat.id) {
            activeCategoryRef.current = cat.id;
            setActiveCategoryId(cat.id);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      if (clickScrollTimeoutRef.current) {
        clearTimeout(clickScrollTimeoutRef.current);
      }
    };
  }, [props.categories]);

  const topOffset =
    (props.areaAboveNavBar ?? 0) + (props.searchWrapperBarHeight ?? 0);

  return (
    <div
      id={BAR_ID}
      className={`${styles.category_bar} ${
        !isFranchiseOpen ? styles.closed : ""
      } ${isMainDark ? "bg__zero" : "bg__02"} ${
        isVisitor() ? styles.extra_top_space : ""
      } ${props.leading ? styles.has_leading : ""} ${
        props.leadingExpanded ? styles.leading_expanded : ""
      }`}
      style={{ top: `${topOffset}px` }}
    >
      {props.leading && <div className={styles.leading}>{props.leading}</div>}
      <ul className={styles.category_bar__list}>
        {guestBuiltOffers.length > 0 && (
          <li>
            <PTab
              href={`#category_${SPECIALLY_FOR_YOU_ANCHOR}`}
              id={`navElm_new_${SPECIALLY_FOR_YOU_ANCHOR}`}
              isActive={activeCategoryId === SPECIALLY_FOR_YOU_ANCHOR}
              tone="loyalty"
              onClick={(e) => {
                activeCategoryRef.current = SPECIALLY_FOR_YOU_ANCHOR;
                setActiveCategoryId(SPECIALLY_FOR_YOU_ANCHOR);
                scrollToCategory(e, props.searchWrapperBarHeight);
              }}
            >
              {t("offers:specially-for-you")}
            </PTab>
          </li>
        )}
        {props.categories?.map((cat) => (
          <li key={cat.id}>
            <PTab
              href={`#category_${cat.id}`}
              id={`navElm_new_${cat.id}`}
              isActive={activeCategoryId === cat.id}
              onClick={(e) => {
                activeCategoryRef.current = cat.id;
                setActiveCategoryId(cat.id);

                isClickScrollingRef.current = true;
                if (clickScrollTimeoutRef.current) {
                  clearTimeout(clickScrollTimeoutRef.current);
                }
                clickScrollTimeoutRef.current = setTimeout(() => {
                  isClickScrollingRef.current = false;
                }, 700);

                scrollToCategory(e, props.searchWrapperBarHeight);
                trackCategoryClick(Number(cat.id));
              }}
            >
              {getDisplayedName(cat, DISPLAYED_NAME.CATEGORY_NAME)}
            </PTab>
          </li>
        ))}
      </ul>
    </div>
  );
};
